import React from 'react';
import { Card, Divider, Typography, theme } from 'antd';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { FiltersForPrint } from '~components/Filters';
import { Page, PageContent } from '~components/PrintableLayout';
import CDNAnalyticsResponseTimeChart, {
  TOOLTIP_MESSAGE as CHART_RESPONSE_TIME_TOOLTIP_MESSAGE,
} from './ResponseTimeChart';
import CDNAnalyticsVolumeChart, { TOOLTIP_MESSAGE as CHART_VOLUME_TOOLTIP_MESSAGE } from './VolumeChart';
import { IsPrintingProvider } from '~library/print';
import { purifyHtml } from '~library/purifyHtml';

type PrintableCDNAnalyticsProps = {
  filters: {
    domain?: string;
    crawlers: string[];
    dateFrom: Date;
    dateTo: Date;
  };
  charts: {
    volume: {
      rawData?: AggregatedMetrics.PagesCrawledVolumeResult;
      isLoading: boolean;
      isError: boolean;
      onRetry: () => void;
    };
    responseTime: {
      rawData?: AggregatedMetrics.CacheHitMissTimeResult;
      isLoading: boolean;
      isError: boolean;
      onRetry: () => void;
    };
  };
  noDataFallback: React.ReactNode;
};

function PrintableCDNAnalytics({ filters, charts, noDataFallback }: PrintableCDNAnalyticsProps) {
  const { token } = theme.useToken();

  return (
    <IsPrintingProvider initialValue={true}>
      <>
        <Page>
          <PageContent>
            <FiltersForPrint
              domainSelected={filters.domain}
              crawlersSelected={filters.crawlers}
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
            />

            <Divider />
            <Typography.Paragraph>
              <Typography.Text strong>Volume: </Typography.Text>
              <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_VOLUME_TOOLTIP_MESSAGE) }} />
            </Typography.Paragraph>
            <Card
              title="Crawler Visits - Volume"
              style={{ marginBottom: token.margin }}
              styles={{ body: { paddingBlock: 0, paddingInline: token.paddingSM } }}
            >
              <CDNAnalyticsVolumeChart
                rawData={charts.volume.rawData}
                isLoading={charts.volume.isLoading}
                isError={charts.volume.isError}
                onRetry={charts.volume.onRetry}
                noDataFallback={noDataFallback}
              />
            </Card>
          </PageContent>
        </Page>
        <Page>
          <PageContent>
            <Typography.Paragraph>
              <Typography.Text strong>Response Time: </Typography.Text>
              <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_RESPONSE_TIME_TOOLTIP_MESSAGE) }} />
            </Typography.Paragraph>
            <Card
              title="Crawler Visits - Response Time"
              styles={{ body: { paddingBlock: 0, paddingInline: token.paddingSM } }}
            >
              <CDNAnalyticsResponseTimeChart
                rawData={charts.responseTime.rawData}
                isLoading={charts.responseTime.isLoading}
                isError={charts.responseTime.isError}
                onRetry={charts.responseTime.onRetry}
                noDataFallback={noDataFallback}
              />
            </Card>
          </PageContent>
        </Page>
      </>
    </IsPrintingProvider>
  );
}

export default PrintableCDNAnalytics;
